import {
  usePostRentNow,
  useGetTrialBalance,
  type TrialBalanceRowOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const amount = (value: string) => (Number(value) ? formatMoney(value) : '');

const COLUMNS: DataColumn<TrialBalanceRowOut>[] = [
  { label: 'Code', render: (r) => r.code },
  { label: 'Account', render: (r) => r.name },
  { label: 'Debit', align: 'right', render: (r) => amount(r.debit) },
  { label: 'Credit', align: 'right', render: (r) => amount(r.credit) },
];

export function TrialBalancePage() {
  const [asOf, setAsOf] = useState('');
  const queryClient = useQueryClient();
  const canPost = useCan('payments.manage');
  const tb = okData(useGetTrialBalance(asOf ? { as_of: asOf } : undefined).data);
  const post = usePostRentNow({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const posted = okData(post.data)?.message;
  return (
    <Stack spacing={2}>
      <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Trial Balance</Typography>
          <Typography color="text.secondary">Every account's balance from the ledger.</Typography>
        </div>
        <TextField
          size="small"
          type="date"
          label="As Of"
          value={asOf}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => setAsOf(e.target.value)}
        />
        {canPost && (
          <Button variant="outlined" disabled={post.isPending} onClick={() => post.mutate()}>
            Post this month's rent
          </Button>
        )}
      </Stack>
      {posted && <Alert severity="info">{posted}</Alert>}
      {post.isError && <Alert severity="error">{apiErrorMessage(post.error)}</Alert>}
      {tb && (
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            color={tb.balanced ? 'success' : 'error'}
            label={tb.balanced ? 'Debits equal credits' : 'Debits and credits differ'}
          />
          <Chip
            color={tb.receivables_tie_to_leases ? 'success' : 'error'}
            label={
              tb.receivables_tie_to_leases
                ? 'Receivables match the lease balances'
                : 'Receivables differ from the lease balances'
            }
          />
        </Stack>
      )}
      <DataTable columns={COLUMNS} rows={tb?.rows ?? []} rowKey={(r) => r.code} />
      {tb && (
        <Stack direction="row" spacing={4} sx={{ justifyContent: 'flex-end' }}>
          <Typography sx={{ fontWeight: 600 }}>
            Total debits {formatMoney(tb.total_debit)}
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            Total credits {formatMoney(tb.total_credit)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
